import React, { useEffect, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import spformsApi from '../../lib/spformsApi';

const {
  FiX,
  FiPlus,
  FiTrash2,
  FiSave,
  FiSettings,
  FiLoader,
  FiZap,
  FiChevronUp,
  FiChevronDown,
} = FiIcons;

const FIELD_TYPES = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'url', label: 'Web URL' },
  { value: 'select', label: 'Dropdown' },
  { value: 'multiselect', label: 'Multi Select' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'consent', label: 'Consent Checkbox' },
  { value: 'file', label: 'File Upload' },
  { value: 'image', label: 'Image Upload' },
  { value: 'signature', label: 'Signature Text' },
];

const CORE_FIELDS = [
  '',
  'first_name',
  'last_name',
  'date_of_birth',
  'birth_year',
  'gender',
  'player_positions',
  'jersey_size',
  'parent_name',
  'parent_mobile',
  'phone',
  'email',
  'current_team',
  'current_club',
  'medical_notes',
  'comments',
  'consent_accepted',
];

function normalizeOptions(value) {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.join(', ');
    } catch {
      return value;
    }
  }

  return '';
}

function parseOptions(value) {
  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function createKey(label, fallback) {
  const slug = String(label || '')
    .trim()
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return slug || fallback;
}

function sortFields(fields) {
  return [...fields].sort((a, b) => {
    const orderA = Number(a.sort_order || 0);
    const orderB = Number(b.sort_order || 0);

    if (orderA !== orderB) return orderA - orderB;

    return String(a.created_at || '').localeCompare(String(b.created_at || ''));
  });
}

export default function FormFieldsModal({ groupId, tenantId, onClose }) {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadFields = async () => {
    if (!tenantId || !groupId) return;

    try {
      setLoading(true);
      setErrorMessage('');

      const data = await spformsApi.formFieldsList(tenantId, groupId);
      setFields(sortFields(data));
    } catch (error) {
      setErrorMessage(error.message || 'Failed to load form fields');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFields();
  }, [tenantId, groupId]);

  const seedDefaults = async () => {
    const confirmed = window.confirm(
      'Seed default registration fields for this event? Existing default fields will be preserved and updated safely.'
    );

    if (!confirmed) return;

    try {
      setSeeding(true);
      setErrorMessage('');

      await spformsApi.seedDefaultFields(tenantId, groupId);
      await loadFields();
    } catch (error) {
      setErrorMessage(error.message || 'Failed to seed default fields');
    } finally {
      setSeeding(false);
    }
  };

  const addField = () => {
    const timestamp = Date.now();

    setFields((prev) => {
      const nextOrder = (prev.length + 1) * 10;

      return [
        ...prev,
        {
          id: null,
          tenant_id: tenantId,
          intake_group_id: groupId,
          field_key: `field_${timestamp}`,
          label: 'New Question',
          help_text: '',
          field_type: 'text',
          options_json: [],
          required: false,
          active: true,
          sort_order: nextOrder,
          maps_to_core_field: '',
        },
      ];
    });
  };

  const updateFieldState = (index, updates) => {
    setFields((prev) => {
      const next = [...prev];
      const current = next[index];

      const updated = {
        ...current,
        ...updates,
      };

      if (
        Object.prototype.hasOwnProperty.call(updates, 'label') &&
        (!current.id || String(current.field_key || '').startsWith('field_'))
      ) {
        updated.field_key = createKey(updates.label, current.field_key);
      }

      next[index] = updated;

      return next;
    });
  };

  const moveField = (index, direction) => {
    setFields((prev) => {
      const next = [...prev];
      const targetIndex = index + direction;

      if (targetIndex < 0 || targetIndex >= next.length) return prev;

      const current = next[index];
      next[index] = next[targetIndex];
      next[targetIndex] = current;

      return next.map((field, idx) => ({
        ...field,
        sort_order: (idx + 1) * 10,
      }));
    });
  };

  const removeField = async (index) => {
    const field = fields[index];

    if (!field) return;

    if (field.maps_to_core_field) {
      const confirmedCore = window.confirm(
        'This is a core/default field. It is safer to disable it instead of deleting it. Delete permanently anyway?'
      );

      if (!confirmedCore) return;
    }

    if (field.id) {
      const confirmed = window.confirm('Delete this field permanently?');

      if (!confirmed) return;

      try {
        await spformsApi.formFieldDelete(tenantId, field.id);
      } catch (error) {
        setErrorMessage(error.message || 'Failed to delete field');
        return;
      }
    }

    setFields((prev) =>
      prev
        .filter((_, idx) => idx !== index)
        .map((item, idx) => ({
          ...item,
          sort_order: (idx + 1) * 10,
        }))
    );
  };

  const disableField = (index) => {
    updateFieldState(index, {
      active: false,
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setErrorMessage('');

      const normalized = fields.map((field, index) => ({
        ...field,
        tenant_id: tenantId,
        intake_group_id: groupId,
        field_key: createKey(field.field_key || field.label, `field_${index + 1}`),
        label: field.label || `Question ${index + 1}`,
        field_type: field.field_type || 'text',
        options_json: ['select', 'multiselect'].includes(field.field_type)
          ? parseOptions(normalizeOptions(field.options_json))
          : Array.isArray(field.options_json)
          ? field.options_json
          : parseOptions(normalizeOptions(field.options_json)),
        required: Boolean(field.required),
        active: field.active !== false,
        sort_order: (index + 1) * 10,
        maps_to_core_field: field.maps_to_core_field || null,
      }));

      for (const field of normalized) {
        if (field.id) {
          await spformsApi.formFieldUpdate(field);
        } else {
          await spformsApi.formFieldCreate(field);
        }
      }

      onClose();
    } catch (error) {
      setErrorMessage(error.message || 'Failed to save form fields');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-6xl bg-white rounded-[2.5rem] shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-8 border-b bg-gray-50/50">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-indigo-600 flex items-center justify-center text-white shadow-lg shadow-indigo-100">
              <SafeIcon icon={FiSettings} size={24} />
            </div>

            <div>
              <h2 className="text-xl font-black text-gray-900 tracking-tight">
                Form Builder
              </h2>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-1">
                Rearrange, enable, disable, and customize event questions
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={seedDefaults}
              disabled={seeding || loading}
              className="px-5 py-2.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-amber-100 transition-all disabled:opacity-60"
            >
              <SafeIcon
                icon={seeding ? FiLoader : FiZap}
                className={seeding ? 'animate-spin' : ''}
              />
              Seed Defaults
            </button>

            <button
              type="button"
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center hover:bg-gray-200 rounded-full transition-colors"
            >
              <SafeIcon icon={FiX} />
            </button>
          </div>
        </div>

        {errorMessage ? (
          <div className="mx-8 mt-6 rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm font-bold text-red-700">
            {errorMessage}
          </div>
        ) : null}

        <div className="flex-1 overflow-y-auto p-8 space-y-6 bg-gray-50/30">
          {loading ? (
            <div className="py-20 text-center animate-pulse text-gray-400 font-black uppercase tracking-[0.3em] text-xs">
              Fetching form fields...
            </div>
          ) : (
            <>
              {fields.length === 0 ? (
                <div className="rounded-[2rem] border-2 border-dashed border-gray-200 bg-white p-12 text-center">
                  <div className="text-lg font-black text-gray-900">
                    No fields configured yet
                  </div>
                  <p className="mt-2 text-sm font-medium text-gray-400">
                    Seed default fields or add a custom field manually.
                  </p>

                  <button
                    type="button"
                    onClick={seedDefaults}
                    className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-6 py-3 text-xs font-black uppercase tracking-widest text-white shadow-xl shadow-indigo-100"
                  >
                    <SafeIcon icon={FiZap} />
                    Seed Default Fields
                  </button>
                </div>
              ) : null}

              {fields.map((field, idx) => {
                const isCore = Boolean(field.maps_to_core_field);
                const isInactive = field.active === false;

                return (
                  <div
                    key={field.id || field.field_key || idx}
                    className={`p-8 border-2 rounded-[2rem] bg-white hover:border-indigo-200 transition-all shadow-sm ${
                      isInactive ? 'opacity-60 border-gray-200 bg-gray-50' : 'border-gray-100'
                    }`}
                  >
                    <div className="flex flex-col lg:flex-row gap-6">
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-6">
                        <div className="md:col-span-4 space-y-2">
                          <div className="flex items-center gap-2">
                            <label className="label-small">Question Label</label>
                            {isCore ? (
                              <span className="text-[8px] font-black bg-indigo-600 text-white px-1.5 py-0.5 rounded uppercase tracking-tighter">
                                Core
                              </span>
                            ) : (
                              <span className="text-[8px] font-black bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded uppercase tracking-tighter">
                                Custom
                              </span>
                            )}
                          </div>

                          <input
                            value={field.label || ''}
                            onChange={(e) =>
                              updateFieldState(idx, {
                                label: e.target.value,
                              })
                            }
                            className="field-input"
                          />
                        </div>

                        <div className="md:col-span-3 space-y-2">
                          <label className="label-small">Field Key</label>
                          <input
                            value={field.field_key || ''}
                            onChange={(e) =>
                              updateFieldState(idx, {
                                field_key: createKey(e.target.value, field.field_key),
                              })
                            }
                            className="field-input font-mono text-xs"
                          />
                        </div>

                        <div className="md:col-span-3 space-y-2">
                          <label className="label-small">Field Type</label>
                          <select
                            value={field.field_type || 'text'}
                            onChange={(e) =>
                              updateFieldState(idx, {
                                field_type: e.target.value,
                              })
                            }
                            className="field-input"
                          >
                            {FIELD_TYPES.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="md:col-span-2 space-y-2">
                          <label className="label-small">Order</label>
                          <input
                            type="number"
                            value={field.sort_order || (idx + 1) * 10}
                            onChange={(e) =>
                              updateFieldState(idx, {
                                sort_order: Number(e.target.value || 0),
                              })
                            }
                            className="field-input"
                          />
                        </div>

                        <div className="md:col-span-4 space-y-2">
                          <label className="label-small">Mapped Core Field</label>
                          <select
                            value={field.maps_to_core_field || ''}
                            onChange={(e) =>
                              updateFieldState(idx, {
                                maps_to_core_field: e.target.value,
                              })
                            }
                            className="field-input font-mono text-xs text-indigo-600"
                          >
                            {CORE_FIELDS.map((coreField) => (
                              <option key={coreField || 'none'} value={coreField}>
                                {coreField || 'None'}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="md:col-span-8 space-y-2">
                          <label className="label-small">Help Text</label>
                          <input
                            value={field.help_text || ''}
                            onChange={(e) =>
                              updateFieldState(idx, {
                                help_text: e.target.value,
                              })
                            }
                            className="field-input"
                            placeholder="Optional helper text shown under the field or inside the input"
                          />
                        </div>

                        <div className="md:col-span-3 flex items-center gap-3 pt-2">
                          <input
                            type="checkbox"
                            checked={Boolean(field.required)}
                            onChange={(e) =>
                              updateFieldState(idx, {
                                required: e.target.checked,
                              })
                            }
                            className="w-5 h-5 rounded border-2 border-gray-300 text-indigo-600"
                          />
                          <span className="text-xs font-black text-gray-600 uppercase tracking-widest">
                            Required
                          </span>
                        </div>

                        <div className="md:col-span-3 flex items-center gap-3 pt-2">
                          <input
                            type="checkbox"
                            checked={field.active !== false}
                            onChange={(e) =>
                              updateFieldState(idx, {
                                active: e.target.checked,
                              })
                            }
                            className="w-5 h-5 rounded border-2 border-gray-300 text-indigo-600"
                          />
                          <span className="text-xs font-black text-gray-600 uppercase tracking-widest">
                            Active
                          </span>
                        </div>

                        {['select', 'multiselect'].includes(field.field_type) ? (
                          <div className="md:col-span-12 space-y-2">
                            <label className="label-small">Options</label>
                            <input
                              value={normalizeOptions(field.options_json)}
                              onChange={(e) =>
                                updateFieldState(idx, {
                                  options_json: parseOptions(e.target.value),
                                })
                              }
                              placeholder="Option A, Option B, Option C"
                              className="field-input"
                            />
                          </div>
                        ) : null}
                      </div>

                      <div className="flex lg:flex-col gap-2 justify-center lg:border-l lg:pl-6 border-gray-100">
                        <button
                          type="button"
                          onClick={() => moveField(idx, -1)}
                          disabled={idx === 0}
                          className="mini-button disabled:opacity-30"
                          title="Move Up"
                        >
                          <SafeIcon icon={FiChevronUp} />
                        </button>

                        <button
                          type="button"
                          onClick={() => moveField(idx, 1)}
                          disabled={idx === fields.length - 1}
                          className="mini-button disabled:opacity-30"
                          title="Move Down"
                        >
                          <SafeIcon icon={FiChevronDown} />
                        </button>

                        <button
                          type="button"
                          onClick={() => disableField(idx)}
                          className="mini-button text-amber-600 hover:bg-amber-50"
                          title="Disable Field"
                        >
                          Off
                        </button>

                        <button
                          type="button"
                          onClick={() => removeField(idx)}
                          className="mini-button text-red-500 hover:bg-red-50"
                          title="Delete"
                        >
                          <SafeIcon icon={FiTrash2} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              <button
                type="button"
                onClick={addField}
                className="w-full py-8 border-2 border-dashed border-gray-200 rounded-[2rem] flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50/50 transition-all group"
              >
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-indigo-100 transition-colors">
                  <SafeIcon icon={FiPlus} />
                </div>

                <span className="text-xs font-black uppercase tracking-[0.2em]">
                  Add Custom Field
                </span>
              </button>
            </>
          )}
        </div>

        <div className="p-8 border-t bg-gray-50 flex items-center justify-end gap-4">
          <button
            type="button"
            onClick={onClose}
            className="px-8 py-4 text-xs font-black text-gray-500 uppercase tracking-widest hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-12 py-4 bg-indigo-600 text-white rounded-2xl text-xs font-black uppercase tracking-[0.2em] shadow-xl shadow-indigo-200 hover:bg-indigo-700 transition-all flex items-center gap-3 disabled:opacity-60"
          >
            {saving ? (
              <SafeIcon icon={FiLoader} className="animate-spin" />
            ) : (
              <SafeIcon icon={FiSave} />
            )}
            Save Form Fields
          </button>
        </div>

        <style>{`
          .label-small {
            font-size: 10px;
            font-weight: 900;
            color: #9ca3af;
            text-transform: uppercase;
            letter-spacing: 0.2em;
          }
          .field-input {
            width: 100%;
            padding: 0.85rem 1.25rem;
            border-radius: 1rem;
            border: 2px solid #f3f4f6;
            font-size: 0.875rem;
            font-weight: 700;
            background: #fff;
            outline: none;
            transition: border-color 0.2s;
          }
          .field-input:focus {
            border-color: #4f46e5;
          }
          .mini-button {
            width: 2.75rem;
            height: 2.75rem;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 1rem;
            color: #6b7280;
            background: #f3f4f6;
            font-weight: 900;
            transition: all 0.2s;
            font-size: 0.75rem;
          }
          .mini-button:hover {
            background: #e5e7eb;
            color: #111827;
          }
        `}</style>
      </div>
    </div>
  );
}